/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react'
import styled from 'styled-components'
import Chart from 'react-apexcharts'
import { ApexOptions } from 'apexcharts'
import { Row, TableColumn } from '../Table'
import { IoTrendingUp, IoTrendingDown } from 'react-icons/io5'
import { useCustomTheme } from '../../../context/Theme/ThemeContextData'

interface GeralChartCardProps {
  title: string
  color: string
  columns: TableColumn[]
  rows: Row[]
  isCurrency?: boolean
  unit?: string
}

export default function GeralChartCard({
  title,
  color,
  columns,
  rows,
  isCurrency = false,
  unit = '',
}: GeralChartCardProps) {
  const { theme } = useCustomTheme()
  const isLight = theme === 'light'

  // Linhas normais (ano comparado e ano atual)
  const baseRows = rows.filter(
    (r) => !r.isPercentage && !r.label.toLowerCase().includes('meta') && !r.label.includes('dias'),
  )

  const rowMeta = rows.find((r) => r.label.toLowerCase() === 'meta' || r.label.toLowerCase().includes('meta cob'))
  const rowMetaPerc = rows.find((r) => r.label.toLowerCase().includes('meta %'))
  const rowVs = rows.find((r) => r.label.toLowerCase().includes('vs'))

  const categories = columns.map((c) => c.label)

  // Montar séries
  const series: any[] = []
  const colors: string[] = []

  if (baseRows.length >= 2) {
    // Ano de Comparação
    series.push({
      name: baseRows[0].label,
      type: 'column',
      data: baseRows[0].values.map((v) => (isCurrency ? Number(v.toFixed(2)) : Math.round(v))),
    })
    colors.push('#5470c6')

    // Ano Atual
    series.push({
      name: baseRows[1].label,
      type: 'column',
      data: baseRows[1].values.map((v) => (isCurrency ? Number(v.toFixed(2)) : Math.round(v))),
    })
    colors.push(color || '#00E396')
  } else if (baseRows.length === 1) {
    series.push({
      name: baseRows[0].label,
      type: 'column',
      data: baseRows[0].values.map((v) => (isCurrency ? Number(v.toFixed(2)) : Math.round(v))),
    })
    colors.push(color || '#00E396')
  }

  // Linha da Meta se existir
  if (rowMeta && rowMeta.values.some((v) => v > 0)) {
    series.push({
      name: rowMeta.label,
      type: 'line',
      data: rowMeta.values.map((v) => (isCurrency ? Number(v.toFixed(2)) : Math.round(v))),
    })
    colors.push('#feb019')
  }

  const formatValue = (val: number) => {
    if (val === undefined || val === null || isNaN(val)) return '0'
    if (isCurrency) {
      return `R$ ${val.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    }
    return `${Math.round(val).toLocaleString('pt-BR')} ${unit}`.trim()
  }

  const options: ApexOptions = {
    chart: {
      type: 'line',
      height: 320,
      toolbar: { show: false },
      fontFamily: 'Montserrat, sans-serif',
      background: 'transparent',
    },
    theme: {
      mode: isLight ? 'light' : 'dark',
    },
    stroke: {
      width: series.map((s) => (s.type === 'line' ? 3 : 0)),
      curve: 'smooth',
      dashArray: series.map((s) => (s.type === 'line' ? 5 : 0)),
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        columnWidth: '55%',
        dataLabels: {
          position: 'top',
        },
      },
    },
    colors,
    xaxis: {
      categories,
      labels: {
        style: {
          colors: isLight ? '#475569' : '#e0e0e0',
          fontSize: '11px',
          fontWeight: 600,
        },
      },
      axisBorder: { color: isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.1)' },
      axisTicks: { color: isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.1)' },
    },
    yaxis: {
      labels: {
        style: {
          colors: isLight ? '#64748b' : '#a0aec0',
          fontSize: '10px',
        },
        formatter: (val: number) => {
          if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`
          if (val >= 1000) return `${(val / 1000).toFixed(0)}k`
          return Math.round(val).toString()
        },
      },
    },
    tooltip: {
      theme: isLight ? 'light' : 'dark',
      shared: true,
      intersect: false,
      y: {
        formatter: (val: number) => formatValue(val),
      },
    },
    legend: {
      position: 'top',
      horizontalAlign: 'right',
      labels: { colors: isLight ? '#0f172a' : '#ffffff' },
      fontSize: '12px',
      fontWeight: 600,
      markers: { size: 6, shape: 'circle' },
    },
    grid: {
      borderColor: isLight ? '#e2e8f0' : 'rgba(255, 255, 255, 0.06)',
      strokeDashArray: 4,
      padding: { top: 10, right: 10, bottom: 0, left: 10 },
    },
    dataLabels: {
      enabled: false,
    },
  }

  // Cards de Totais
  const totalPrincipal = baseRows.length > 1 ? baseRows[1].total : (baseRows[0]?.total || 0)
  const totalComp = baseRows.length > 1 ? baseRows[0].total : 0
  const vsTotal = rowVs ? rowVs.total : totalComp > 0 ? ((totalPrincipal / totalComp) - 1) * 100 : 0
  const metaTotalPerc = rowMetaPerc ? rowMetaPerc.total : 0

  return (
    <CardContainer>
      <CardHeader color={color}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <ColorIndicator color={color} />
          <CardTitle>{title}</CardTitle>
        </div>

        <HeaderStats>
          {rowVs && (
            <StatBadge isPositive={vsTotal >= 0}>
              {vsTotal >= 0 ? <IoTrendingUp size={14} /> : <IoTrendingDown size={14} />}
              {rowVs.label}: {vsTotal > 0 ? '+' : ''}
              {vsTotal.toFixed(1)}%
            </StatBadge>
          )}

          {rowMetaPerc && (
            <MetaBadge isPositive={metaTotalPerc >= 0}>
              Meta: {metaTotalPerc > 0 ? '+' : ''}
              {metaTotalPerc.toFixed(1)}%
            </MetaBadge>
          )}

          <TotalBadge>
            Total: <strong>{formatValue(totalPrincipal)}</strong>
          </TotalBadge>
        </HeaderStats>
      </CardHeader>

      <ChartWrapper>
        <Chart options={options} series={series} type='line' height={320} />
      </ChartWrapper>
    </CardContainer>
  )
}

const CardContainer = styled.div`
  background: ${(props) =>
    props.theme.name === 'light' ? '#ffffff' : 'rgba(0, 29, 83, 0.4)'};
  backdrop-filter: blur(10px);
  border: 1px solid
    ${(props) =>
      props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.08)'};
  border-radius: 14px;
  margin-bottom: 20px;
  overflow: hidden;
  box-shadow: ${(props) =>
    props.theme.name === 'light'
      ? '0 4px 16px rgba(0, 0, 0, 0.06)'
      : '0 8px 24px rgba(0, 0, 0, 0.25)'};
  transition: all 0.3s ease;

  &:hover {
    border-color: ${(props) =>
      props.theme.name === 'light' ? '#0284c7' : 'rgba(166, 193, 11, 0.3)'};
    box-shadow: ${(props) =>
      props.theme.name === 'light'
        ? '0 6px 20px rgba(0, 0, 0, 0.1)'
        : '0 10px 30px rgba(0, 0, 0, 0.35)'};
  }
`

const CardHeader = styled.div<{ color: string }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
  background: ${(props) =>
    props.theme.name === 'light' ? '#f8fafc' : 'rgba(0, 20, 60, 0.6)'};
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'light' ? '#e2e8f0' : 'rgba(255, 255, 255, 0.06)'};
  flex-wrap: wrap;
  gap: 12px;
`

const ColorIndicator = styled.div<{ color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${(props) => props.color || '#a6c10b'};
  box-shadow: 0 0 8px ${(props) => props.color || '#a6c10b'};
`

const CardTitle = styled.h3`
  font-size: 14px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#ffffff')};
  margin: 0;
  font-family: 'Montserrat', sans-serif;
  letter-spacing: 0.5px;
`

const HeaderStats = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`

const StatBadge = styled.div<{ isPositive: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 700;
  font-family: 'Montserrat', sans-serif;
  background: ${(props) => (props.isPositive ? 'rgba(0, 227, 150, 0.15)' : 'rgba(255, 69, 96, 0.15)')};
  color: ${(props) => (props.isPositive ? '#00E396' : '#FF4560')};
  border: 1px solid ${(props) => (props.isPositive ? 'rgba(0, 227, 150, 0.3)' : 'rgba(255, 69, 96, 0.3)')};
`

const MetaBadge = styled.div<{ isPositive: boolean }>`
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 700;
  font-family: 'Montserrat', sans-serif;
  background: ${(props) => (props.isPositive ? 'rgba(254, 176, 25, 0.15)' : 'rgba(255, 69, 96, 0.15)')};
  color: ${(props) => (props.isPositive ? '#FEB019' : '#FF4560')};
  border: 1px solid ${(props) => (props.isPositive ? 'rgba(254, 176, 25, 0.3)' : 'rgba(255, 69, 96, 0.3)')};
`

const TotalBadge = styled.div`
  padding: 4px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-family: 'Montserrat', sans-serif;
  background: ${(props) =>
    props.theme.name === 'light' ? '#ffffff' : 'rgba(255, 255, 255, 0.08)'};
  color: ${(props) => (props.theme.name === 'light' ? '#0f172a' : '#ffffff')};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'light' ? '#cbd5e1' : 'rgba(255, 255, 255, 0.12)'};

  strong {
    color: ${(props) => (props.theme.name === 'light' ? '#0284c7' : '#a6c10b')};
    margin-left: 4px;
  }
`

const ChartWrapper = styled.div`
  padding: 16px 14px 8px 14px;
`
